import React, { createContext, useContext, useState, ReactNode } from 'react'
import { Pattern, usePatternContext } from './PatternContext'

// Side panel tab types
export type PatternSidePanelTab = 'alerts' | 'backtests' | 'version'

interface PatternSidePanelContextType {
  selectedPattern: Pattern | null
  setSelectedPattern: React.Dispatch<React.SetStateAction<Pattern | null>>
  isSidePanelOpen: boolean
  openSidePanel: (pattern: Pattern) => void
  closeSidePanel: () => void
  activeSidePanelTab: PatternSidePanelTab
  setActiveSidePanelTab: (tab: PatternSidePanelTab) => void
  updateSelectedPattern: (updatedPattern: Pattern) => void
}

const PatternSidePanelContext = createContext<PatternSidePanelContextType | undefined>(undefined)

export const usePatternSidePanelContext = () => {
  const context = useContext(PatternSidePanelContext)
  if (context === undefined) {
    throw new Error('usePatternSidePanelContext must be used within a PatternSidePanelProvider')
  }
  return context
}

interface PatternSidePanelProviderProps {
  children: ReactNode
}

export const PatternSidePanelProvider: React.FC<PatternSidePanelProviderProps> = ({ children }) => {
  const [selectedPattern, setSelectedPattern] = useState<Pattern | null>(null)
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false)
  const [activeSidePanelTab, setActiveSidePanelTab] = useState<PatternSidePanelTab>('alerts')

  const { setFocusedBacktest } = usePatternContext()

  const openSidePanel = (pattern: Pattern) => {
    if (selectedPattern?.id !== pattern.id) {
      closeSidePanel()
    }

    setFocusedBacktest(null)
    setSelectedPattern(pattern)
    setIsSidePanelOpen(true)
    setTimeout(() => {
      scrollToSelectedPattern(pattern)
    }, 250)
  }

  const scrollToSelectedPattern = (pattern: Pattern) => {
    if (pattern) {
      // Find the pattern card element by class and ID
      setTimeout(() => {
        const patternCardElement = document.querySelector(
          `.pattern-card[data-pattern-id="${pattern.id}"]`
        )
        const containerElement = document.getElementById('pattern-card-list-container')

        if (patternCardElement && containerElement) {
          // Get the position of the pattern card relative to the container
          const containerRect = containerElement.getBoundingClientRect()
          const patternRect = patternCardElement.getBoundingClientRect()

          // Calculate the scroll position
          const scrollTop =
            patternRect.top -
            containerRect.top +
            containerElement.scrollTop -
            containerRect.height / 2 +
            patternRect.height / 2

          // Scroll the container to the pattern card
          containerElement.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
          })

          // Highlight the pattern card briefly
          patternCardElement.classList.add('highlight-pattern')
          setTimeout(() => {
            patternCardElement.classList.remove('highlight-pattern')
          }, 1500)
        }
      }, 100) // Small delay to ensure the DOM has updated
    }
  }

  const closeSidePanel = () => {
    setIsSidePanelOpen(false)
    setSelectedPattern(null)
  }

  const updateSelectedPattern = (updatedPattern: Pattern) => {
    setSelectedPattern(updatedPattern)
  }

  const value = {
    selectedPattern,
    setSelectedPattern,
    isSidePanelOpen,
    openSidePanel,
    closeSidePanel,
    activeSidePanelTab,
    setActiveSidePanelTab,
    updateSelectedPattern,
  }

  return (
    <PatternSidePanelContext.Provider value={value}>{children}</PatternSidePanelContext.Provider>
  )
}
